import type { SymbolOptions } from 'milsymbol';
import ms from 'milsymbol';
import { Icon } from 'ol/style';
import Style from 'ol/style/Style';

import { isNonEmptyString } from '@nora-monorepo/shared';
import { appLogger } from '@nora-monorepo/ui-shared';

const DEFAULT_SYMBOL_SIZE = 30;
const HEADING_CACHE_RESOLUTION_DEGREES = 5;

interface GetMilSymbolStyleOptions {
  sidc: string;
  heading?: number;
  speed?: number;
  callSign?: string;
  size?: number;
}

export interface MilSymbolImage {
  src: string;
  width: number;
  height: number;
  anchor: [number, number];
}

/**
 * Builds the SVG for a SIDC, mapping heading to direction, call sign to unique
 * designation and speed. Returns null when the SIDC is empty or invalid.
 */
export function buildMilSymbolImage(options: GetMilSymbolStyleOptions): MilSymbolImage | null {
  const { sidc, heading, speed, callSign, size = DEFAULT_SYMBOL_SIZE } = options;

  if (!isNonEmptyString(sidc)) {
    return null;
  }

  try {
    const symbolOptions: SymbolOptions = { size };

    if (isNonEmptyString(callSign)) {
      symbolOptions.uniqueDesignation = callSign;
    }

    if (heading != null) {
      symbolOptions.direction = heading;
    }

    if (speed != null) {
      symbolOptions.speed = `${Math.round(speed)}`;
    }

    const symbol = new ms.Symbol(sidc, symbolOptions);

    if (symbol.isValid() !== true) {
      return null;
    }

    const { width, height } = symbol.getSize();
    const anchor = symbol.getAnchor();

    return {
      src: `data:image/svg+xml,${encodeURIComponent(symbol.asSVG())}`,
      width,
      height,
      anchor: [anchor.x / width, anchor.y / height],
    };
  }
  catch (error) {
    appLogger.warn({
      message: 'Milsymbol graphic generation failed',
      sensitiveContext: {
        sidc,
        error: error instanceof Error ? { name: error.name, message: error.message } : { value: String(error) },
      },
    });

    return null;
  }
}

export class MilSymbolStyleManager {
  private static readonly styleCache = new Map<string, Style>();

  /**
   * Renders a SIDC as a milsymbol icon. Returns null when the SIDC is invalid.
   */
  static getMilSymbolGraphic(options: GetMilSymbolStyleOptions): Style | null {
    if (!isNonEmptyString(options.sidc)) {
      return null;
    }

    const cacheKey = MilSymbolStyleManager.buildCacheKey(options);
    const cached = MilSymbolStyleManager.styleCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    const image = buildMilSymbolImage(options);

    if (!image) {
      return null;
    }

    const style = new Style({
      image: new Icon({
        src: image.src,
        width: image.width,
        height: image.height,
        anchor: image.anchor,
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
      }),
    });

    MilSymbolStyleManager.styleCache.set(cacheKey, style);

    return style;
  }

  private static buildCacheKey(options: GetMilSymbolStyleOptions): string {
    const { sidc, heading, speed, callSign, size = DEFAULT_SYMBOL_SIZE } = options;

    // round heading so a constantly changing value doesn't blow up the cache
    const roundedHeading = heading != null
      ? Math.round(heading / HEADING_CACHE_RESOLUTION_DEGREES) * HEADING_CACHE_RESOLUTION_DEGREES
      : '';
    const roundedSpeed = speed != null ? Math.round(speed) : '';

    return `${sidc}:${size}:${roundedHeading}:${roundedSpeed}:${callSign ?? ''}`;
  }
}
